import { z } from 'zod';
import type { TCRealtimePayload } from '../types/realtime-payload.types';
import { schema } from '../../../../../api/generated/schemas/CommunicationsRealtimePayload';

/**
 * Preserve canonical envelope stripping; JSON Schema models additional keys as
 * either allowed or rejected and cannot express Zod's strip behavior.
 * @param validator Generated envelope union (nested resources are left intact).
 * @returns Flat generated variants with unknown envelope fields stripped.
 * @throws If the generated root stops being an object union.
 */
function stripEnvelopeVariants(validator: z.core.$ZodType): z.ZodType[] {
  if (validator instanceof z.ZodUnion) {
    return validator.options.flatMap(stripEnvelopeVariants);
  }
  if (validator instanceof z.ZodObject) return [validator.strip()];
  throw new Error('Expected generated socket envelope objects');
}

/**
 * Validate the generated wire shape before binding its generated TypeScript type.
 * JSON Schema omits cross-field refinements; the adapter below retains them.
 */
export const communicationsRealtimePayloadSchema = z
  .union(
    stripEnvelopeVariants(
      z.fromJSONSchema(schema, { defaultTarget: 'openapi-3.0' })
    )
  )
  .pipe(z.custom<TCRealtimePayload>())
  .superRefine((payload, context) => {
    const resource = payload.resource;
    // Message's shared dates are plain strings. Cache ordering requires ISO dates.
    for (const field of ['createdAt', 'updatedAt'] as const) {
      if (
        resource &&
        'createdAt' in resource &&
        'updatedAt' in resource &&
        !z.iso.datetime().safeParse(resource[field]).success
      ) {
        context.addIssue({
          code: 'custom',
          path: ['resource', field],
          message: 'Resource cache timestamps must be ISO dates',
        });
      }
    }
    if (resource && resource.tenantId !== payload.tenantId) {
      context.addIssue({
        code: 'custom',
        path: ['resource', 'tenantId'],
        message: 'Resource tenant must match envelope',
      });
    }
    if (resource && String(resource.id) !== payload.resourceId) {
      context.addIssue({
        code: 'custom',
        path: ['resource', 'id'],
        message: 'Resource id must match envelope',
      });
    }
    if (
      resource &&
      'funnelConversationId' in resource &&
      resource.funnelConversationId != null &&
      resource.funnelConversationId !== payload.funnelConversationId
    ) {
      context.addIssue({
        code: 'custom',
        path: ['resource', 'funnelConversationId'],
        message: 'Resource conversation must match envelope',
      });
    }
    if (
      payload.resourceType === 'Conversation' &&
      payload.resource &&
      payload.resource.id !== payload.funnelConversationId
    ) {
      context.addIssue({
        code: 'custom',
        path: ['funnelConversationId'],
        message: 'Conversation id must match envelope',
      });
    }
  })
  .superRefine((payload, context) => {
    const resource = payload.resource;
    if (
      resource &&
      'funnelId' in resource &&
      payload.funnelId !== undefined &&
      resource.funnelId !== payload.funnelId
    ) {
      context.addIssue({
        code: 'custom',
        path: ['resource', 'funnelId'],
        message: 'Resource funnel must match envelope',
      });
    }
    if (resource && payload.messagingConversationId !== undefined) {
      const conversationId =
        'conversationId' in resource
          ? resource.conversationId
          : 'messagingConversationId' in resource
            ? resource.messagingConversationId
            : undefined;
      if (
        conversationId != null &&
        conversationId !== payload.messagingConversationId
      ) {
        context.addIssue({
          code: 'custom',
          path: ['messagingConversationId'],
          message: 'Messaging conversation must match envelope',
        });
      }
    }
  })
  .superRefine((payload, context) => {
    if (payload.resourceType === 'Conversation') {
      if (
        payload.document &&
        (payload.document.tenantId !== payload.tenantId ||
          payload.document.id !== String(payload.funnelConversationId))
      ) {
        context.addIssue({
          code: 'custom',
          path: ['document'],
          message: 'Document identity must match envelope',
        });
      }
    }
  });
